<template>
  <div class="tw-max-w-2xl tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-5">
    <h2 class="tw-text-lg tw-font-bold tw-text-gray-800 tw-mb-1">
      {{ $t('subscriptions.settings.title', 'WooCommerce webhook') }}
    </h2>
    <p class="tw-text-sm tw-text-gray-500 tw-mb-5">
      {{ $t('subscriptions.settings.subtitle', 'Receive completed orders from the store and provision subscriptions.') }}
    </p>

    <!-- Enable toggle -->
    <label class="tw-flex tw-items-center tw-gap-3 tw-mb-5 tw-cursor-pointer">
      <input type="checkbox" v-model="form.webhook_enabled" class="tw-w-5 tw-h-5 tw-accent-primary-500" />
      <span class="tw-text-sm tw-font-semibold tw-text-gray-700">
        {{ $t('subscriptions.settings.enabled', 'Webhook enabled') }}
      </span>
    </label>

    <!-- Store URL -->
    <div class="tw-mb-4">
      <FormField
        v-model="form.store_url"
        type="url"
        input-dir="ltr"
        :label="$t('subscriptions.settings.store-url', 'Store URL (used for the “Renew” button)')"
        placeholder="https://store.example.com"
        :hide-errors="true"
      />
    </div>

    <!-- Secret (read-only) -->
    <div class="tw-mb-4">
      <label class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
        {{ $t('subscriptions.settings.secret', 'Shared secret') }}
      </label>
      <div class="tw-flex tw-items-center tw-gap-2">
        <input
          :value="data.webhook_secret || $t('subscriptions.settings.no-secret', 'Not generated yet')"
          readonly
          class="tw-flex-1 tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-bg-gray-50 tw-font-mono tw-text-gray-700"
        />
        <a-button type="button" intent="flat" @click="copy(data.webhook_secret)" v-if="data.webhook_secret">
          {{ $t('common.copy', 'Copy') }}
        </a-button>
      </div>
      <label class="tw-flex tw-items-center tw-gap-2 tw-mt-2 tw-cursor-pointer">
        <input type="checkbox" v-model="form.regenerate_secret" class="tw-w-4 tw-h-4 tw-accent-primary-500" />
        <span class="tw-text-xs tw-text-gray-500">{{ $t('subscriptions.settings.regenerate', 'Regenerate secret on save (updates the WordPress plugin too)') }}</span>
      </label>
    </div>

    <!-- Endpoint URLs -->
    <div class="tw-mb-5 tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-xs tw-text-gray-600 tw-space-y-1">
      <div class="tw-flex tw-items-center tw-gap-2">
        <span class="tw-font-semibold">{{ $t('subscriptions.settings.order-url', 'Order endpoint') }}:</span>
        <code class="tw-break-all">{{ data.webhook_url }}</code>
      </div>
      <div class="tw-flex tw-items-center tw-gap-2">
        <span class="tw-font-semibold">{{ $t('subscriptions.settings.plans-url', 'Plans endpoint') }}:</span>
        <code class="tw-break-all">{{ data.plans_sync_url }}</code>
      </div>
    </div>

    <a-button intent="primary" :loading="saving" :disabled="saving" @click="save">
      {{ $t('buttons.save', 'Save') }}
    </a-button>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue';
import FormField from '@/components/form/FormField.vue';
import subscriptionsApi from '@/api/subscriptions';
import useAlert from '@/composables/useAlert';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const data = reactive({ webhook_secret: null, webhook_url: '', plans_sync_url: '' });
const form = reactive({ webhook_enabled: false, store_url: '', regenerate_secret: false });
const saving = ref(false);

const load = async () => {
  try {
    const res = await subscriptionsApi.admin.getSettings();
    const d = res.data.data;
    data.webhook_secret = d.webhook_secret;
    data.webhook_url = d.webhook_url;
    data.plans_sync_url = d.plans_sync_url;
    form.webhook_enabled = d.webhook_enabled;
    form.store_url = d.store_url || '';
  } catch (e) {
    useAlert(t('errors.something-wrong-happened'), 'danger');
  }
};

const save = async () => {
  saving.value = true;
  try {
    const res = await subscriptionsApi.admin.updateSettings({
      webhook_enabled: form.webhook_enabled,
      store_url: form.store_url || null,
      regenerate_secret: form.regenerate_secret,
    });
    const d = res.data.data;
    data.webhook_secret = d.webhook_secret;
    form.regenerate_secret = false;
    useAlert(t('subscriptions.settings.saved', 'Settings saved'), 'success');
  } catch (e) {
    useAlert(t('errors.something-wrong-happened'), 'danger');
  } finally {
    saving.value = false;
  }
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    useAlert(t('common.copied', 'Copied'), 'success');
  } catch (e) { /* noop */ }
};

onMounted(load);
</script>
